import { createServerFn } from "@tanstack/react-start";
import { requireAuthLazy } from "@/lib/require-auth-lazy";
import { leituraPublicaPermitida } from "@/lib/banco-externo-publico";
import { lerConfiguracao } from "@/lib/banco-externo-config.server";
import { executarNoBancoExterno } from "@/lib/banco-externo.server";
import { registrarLog, registrarLogUmaVez } from "@/lib/log-servidor.server";

export type EstadoBancoExterno = {
  ativo: boolean;
  host: string;
  porta: number;
  usuario: string;
  banco: string;
  ssl: boolean;
  temSenha: boolean;
};

export type EstadoBancoExternoPublico = {
  ativo: boolean;
};

export type RelacaoExterna = {
  tabela: string;
  campo: string;
  tabela_referenciada: string;
  campo_referenciado: string;
};

/** Situação atual da conexão externa (nunca devolve a senha). */
export const estadoBancoExterno = createServerFn({ method: "GET" })
  .middleware([requireAuthLazy])
  .handler(async ({ context }): Promise<EstadoBancoExterno> => {
    const cfg = await lerConfiguracao();
    return {
      ativo: cfg.ativo,
      host: cfg.host,
      porta: cfg.porta,
      usuario: cfg.usuario,
      banco: cfg.banco,
      ssl: cfg.ssl,
      temSenha: !!cfg.senha,
    };
  });

/** Situação mínima do banco, usada antes do login para escolher a ponte correta. */
export const estadoBancoExternoPublico = createServerFn({ method: "GET" }).handler(
  async (): Promise<EstadoBancoExternoPublico> => {
    const { lerConfiguracao } = await import("@/lib/banco-externo-config.server");
    const cfg = await lerConfiguracao();
    return { ativo: cfg.ativo };
  },
);

/** Testa a conexão com o banco externo e devolve a versão do servidor. */
export const testarBancoExterno = createServerFn({ method: "POST" })
  .middleware([requireAuthLazy])
  .handler(async ({ context }: any): Promise<{ ok: boolean; mensagem: string }> => {
    const cfg = await lerConfiguracao();
    const { executarNoBancoExterno: exec } = { executarNoBancoExterno };

    if (!cfg.host) {
      registrarLog("banco-externo", "Teste de conexão recusado: endereço do servidor vazio.", {
        nivel: "erro",
      });
      return { ok: false, mensagem: "Informe o endereço do servidor." };
    }
    try {
      const linhas = await executarNoBancoExterno(
        cfg,
        "select version() as versao",
        [],
        context?.userId,
      );
      registrarLog(
        "banco-externo",
        `Teste de conexão bem-sucedido: ${String(linhas[0]?.["versao"] ?? "conectado")}.`,
        { nivel: "sucesso" },
      );
      return { ok: true, mensagem: String(linhas[0]?.["versao"] ?? "conectado") };
    } catch (e) {
      registrarLog("banco-externo", `Teste de conexão falhou: ${(e as Error).message}`, {
        nivel: "erro",
        detalhes: e,
      });
      return { ok: false, mensagem: (e as Error).message };
    }
  });

/** Executa uma consulta/comando SQL no banco externo. */
export const consultarBancoExterno = createServerFn({ method: "POST" })
  .validator((input: { sql: string; parametros?: unknown[] }) => {
    if (typeof input?.sql !== "string" || input.sql.trim() === "") {
      throw new Error("SQL não informado.");
    }
    return { sql: input.sql, parametros: input.parametros ?? [] };
  })
  .middleware([requireAuthLazy])
  .handler(async ({ data, context }: any): Promise<{ linhas: Record<string, any>[] }> => {
    const cfg = await lerConfiguracao();
    if (!cfg.ativo) {
      registrarLogUmaVez(
        "consulta-inativo",
        "banco-externo",
        "Consulta recusada: o banco de dados externo não está ativo.",
        { nivel: "erro" },
      );
      throw new Error("O banco de dados externo não está ativo.");
    }
    try {
      const linhas = await executarNoBancoExterno(cfg, data.sql, data.parametros, context?.userId);
      return { linhas };
    } catch (e) {
      registrarLog(
        "banco-externo",
        `Erro ao executar consultarBancoExterno: ${(e as Error).message}`,
        {
          nivel: "erro",
          detalhes: {
            sql: data.sql?.slice?.(0, 1000) ?? data.sql,
            parametros: data.parametros,
            userId: context?.userId,
          },
        },
      );
      // Re-throw to keep server function behavior (500) but with logged details.
      throw e;
    }
  });

/**
 * Leitura pública (sem login) no banco externo, restrita a consultas simples
 * nas tabelas/funções liberadas — usada pela tela de entrada.
 */
export const consultarBancoExternoPublico = createServerFn({ method: "POST" })
  .validator((input: { sql: string; parametros?: unknown[] }) => {
    if (typeof input?.sql !== "string" || input.sql.trim() === "") {
      throw new Error("SQL não informado.");
    }
    if (!leituraPublicaPermitida(input.sql)) {
      throw new Error("Unauthorized: consulta não liberada sem login.");
    }
    return { sql: input.sql, parametros: input.parametros ?? [] };
  })
  .handler(async ({ data }): Promise<{ linhas: Record<string, any>[] }> => {
    const { lerConfiguracao } = await import("@/lib/banco-externo-config.server");
    const { executarNoBancoExterno } = await import("@/lib/banco-externo.server");
    const cfg = await lerConfiguracao();
    if (!cfg.ativo) throw new Error("O banco de dados externo não está ativo.");
    const linhas = await executarNoBancoExterno(cfg, data.sql, data.parametros);
    return { linhas };
  });

/** Vínculos (chaves estrangeiras) do banco externo, usados para leituras aninhadas. */
export const relacoesBancoExterno = createServerFn({ method: "GET" })
  .middleware([requireAuthLazy])
  .handler(async ({ context }: any): Promise<RelacaoExterna[]> => {
    const { lerConfiguracao } = await import("@/lib/banco-externo-config.server");
    const { executarNoBancoExterno } = await import("@/lib/banco-externo.server");
    const cfg = await lerConfiguracao();
    if (!cfg.ativo) return [];
    const linhas = await executarNoBancoExterno(
      cfg,
      `select tc.table_name as tabela, kcu.column_name as campo,
              ccu.table_name as tabela_referenciada, ccu.column_name as campo_referenciado
         from information_schema.table_constraints tc
         join information_schema.key_column_usage kcu
           on kcu.constraint_name = tc.constraint_name and kcu.table_schema = tc.table_schema
         join information_schema.constraint_column_usage ccu
           on ccu.constraint_name = tc.constraint_name and ccu.table_schema = tc.table_schema
        where tc.constraint_type = 'FOREIGN KEY' and tc.table_schema = 'public'`,
      [],
      context?.userId,
    );
    return linhas as unknown as RelacaoExterna[];
  });
